import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import { useGiftMenuItems } from '@wix/da-shared-react/pkg/gifting/giftMenuItems';
import { useWatchButton } from '@wix/da-shared-react/pkg/WatchButton/useWatchButton';
import AuthorWatchButton from '@wix/da-shared-react/pkg/Button/AuthorWatchButton';
import {
  BiData,
  BiContextProvidedValue,
  GiftClickBiEvent,
  MessageIconClickBiEvent,
} from '@wix/da-bi/pkg/events';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/MailOutline';
import GiftIcon from '@wix/da-ds/pkg/Icons/24x24/StudioGift';
import {
  DropdownOption,
  NullableDropdownItem,
} from '@wix/da-ds/pkg/Dropdown/types';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { initGiftState } from '@wix/da-shared-react/pkg/gifting/redux/actions';

import s from './TopCustomersActionMenu.scss';

/**
 * Note: gift actions require the redux module from `@wix/da-shared-react/pkg/gifting/redux/ModuleLoaderLazy`.
 * Mount it in the consumer component
 */
export default function useContextMenuItems(
  user: PapiUser,
  expandedActions: boolean
): (NullableDropdownItem | ReactNode)[] {
  const { t } = useTranslation();
  const { text: watchText, onClick: onWatchButtonClick } = useWatchButton(
    user,
    user.isWatching
  );
  const dispatchInitGiftState = useAction(initGiftState);

  const handleOpenGiftMenu = useCallback(() => {
    dispatchInitGiftState(user);
  }, [dispatchInitGiftState, user]);

  const giftMenuItems = useGiftMenuItems(user);
  const giftClickBiData = BiData<GiftClickBiEvent>({
    evid: 274,
    recipientid: `${user.useridUuid}`,
  });

  const sendNote = useCallback(() => {
    window.location.assign(Url.userMessageLink(user.username));
  }, [user.username]);

  const sendMessageBiData = BiData<MessageIconClickBiEvent>({
    evid: 470,
    sectionname: BiContextProvidedValue,
    recipientid: `${user.useridUuid}`,
  });
  const sendNoteLabel = t('studio.reports.section.common.actions.sendMessage');

  return expandedActions
    ? [
        <IconButton
          key="note"
          icon={MailIcon}
          size="medium"
          variant="main"
          onClick={sendNote}
          biData={sendMessageBiData}
          aria-label={sendNoteLabel}
          noPaddings
          tooltipText={sendNoteLabel}
        />,
        <IconButtonWithContextMenu
          key="gift"
          icon={GiftIcon}
          size="medium"
          placement="top-start"
          tooltipText={t('studio.reports.section.common.actions.gifting')}
          popperManagerProps={{
            onOpen: handleOpenGiftMenu,
          }}
          aria-label={t('studio.reports.section.common.actions.gifting')}
          items={giftMenuItems}
          biData={giftClickBiData}
        />,
        <AuthorWatchButton
          key="watch"
          author={user}
          className={s['watch-button']}
        />,
      ]
    : [
        {
          items: [
            {
              label: sendNoteLabel,
              onClick: sendNote,
              biData: sendMessageBiData,
            },
            {
              label: t('studio.reports.section.common.actions.gifting'),
              submenu: (
                <StandardMenu
                  id={`top-customer-gifting-${user.userId}`}
                  items={giftMenuItems}
                />
              ),
              subMenuPreferredSide: 'left',
              biData: giftClickBiData,
            },
            {
              label: watchText,
              onClick: onWatchButtonClick,
            },
          ] as DropdownOption[],
        },
      ];
}
